import { useCallback, useEffect, useRef, useState } from "react";

/*
 * The download affordance state machine, ported from the locked mockup's JS
 * (setLoading / setDone / cascadeAll) with the mock timers replaced by REAL
 * downloads. Every button carries rest → loading (spinner) → done ("Saved" ✓)
 * → reset. "Download all" ripples top→bottom over the ready parts and triggers
 * each real part download; the disabled part has no ready number so it is never
 * in the ready list and is skipped automatically. reduced-motion resolves the
 * ripple all-at-once with no per-row stagger.
 */

// Timings lifted verbatim from the mockup so the felt cadence is identical.
const STAGGER = 90; // ms between each part in the cascade ripple
const ROW_LOAD = 620; // ms a row shows the spinner before "Saved"
const ALL_SETTLE_MIN = 900; // floor for how long the all-button stays busy
const SINGLE_LOAD = 900; // single-button spinner duration
const DONE_HOLD_SINGLE = 2200; // single "Saved" hold before reset
const DONE_HOLD_ALL = 2600; // all "Saved" hold before reset
const DONE_HOLD_REDUCED = 2400; // reduced-motion hold before reset

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Trigger a real browser download without navigating away: the rails part
// endpoint 302-redirects to a presigned S3 URL served Content-Disposition:
// attachment, so the browser downloads and keeps the page. (The `download`
// attribute is ignored cross-origin; Content-Disposition is what names the file.)
const triggerBrowserDownload = (url) => {
  if (!url || typeof document === "undefined") return;
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * @param {(partNumber:number)=>string} buildPartUrl - resolves a part's download URL
 */
export const useDownloadFlow = (buildPartUrl) => {
  const [partStates, setPartStates] = useState({}); // partNumber -> 'loading' | 'done'
  const [allState, setAllState] = useState("rest"); // 'rest' | 'loading' | 'done'
  const [announcement, setAnnouncement] = useState("");
  const timers = useRef([]);
  const cascading = useRef(false);

  const later = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
    return id;
  }, []);

  // Cancel any in-flight affordance timers on unmount so no setState fires
  // after the page is gone (e.g. the processing→ready refetch swaps the view).
  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    },
    []
  );

  const setPart = useCallback(
    (n, s) => setPartStates((prev) => ({ ...prev, [n]: s })),
    []
  );
  const clearPart = useCallback(
    (n) =>
      setPartStates((prev) => {
        const next = { ...prev };
        delete next[n];
        return next;
      }),
    []
  );

  // Single part / single-hero: press → loading → "Saved" ✓ → reset. The real
  // download is driven by the anchor's own default navigation (the caller does
  // not preventDefault), so this only runs the visual affordance.
  const runSingle = useCallback(
    (n) => {
      if (partStates[n]) return; // already loading/done — ignore repeat presses
      if (prefersReducedMotion()) {
        setPart(n, "done");
        later(() => clearPart(n), DONE_HOLD_REDUCED);
        return;
      }
      setPart(n, "loading");
      later(() => {
        setPart(n, "done");
        later(() => clearPart(n), DONE_HOLD_SINGLE);
      }, SINGLE_LOAD);
    },
    [partStates, setPart, clearPart, later]
  );

  // "Download all": ripple the ready parts top→bottom, triggering each real
  // download, and settle the all-button once the cascade lands.
  const runAll = useCallback(
    (readyPartNumbers) => {
      if (allState !== "rest") return;
      cascading.current = true;
      setAllState("loading");
      setAnnouncement(`Downloading all ${readyPartNumbers.length} parts`);

      const finish = () => {
        setAllState("done");
        // One polite completion message (no per-row announcement spam).
        setAnnouncement(`All ${readyPartNumbers.length} parts saved`);
        later(() => {
          setPartStates({});
          setAllState("rest");
          cascading.current = false;
        }, DONE_HOLD_ALL);
      };

      if (prefersReducedMotion()) {
        readyPartNumbers.forEach((n) => {
          triggerBrowserDownload(buildPartUrl(n));
          setPart(n, "done");
        });
        finish();
        return;
      }

      readyPartNumbers.forEach((n, i) => {
        later(() => {
          setPart(n, "loading");
          triggerBrowserDownload(buildPartUrl(n));
          later(() => setPart(n, "done"), ROW_LOAD);
        }, i * STAGGER);
      });
      const settle = Math.max(
        (readyPartNumbers.length - 1) * STAGGER + ROW_LOAD + 140,
        ALL_SETTLE_MIN
      );
      later(finish, settle);
    },
    [allState, buildPartUrl, setPart, later]
  );

  const isCascading = useCallback(() => cascading.current, []);

  return { partStates, allState, announcement, runSingle, runAll, isCascading };
};
